import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter 
} from '../../components/ui/dialog';
import { employerAPI } from '../../services/api';
import { toast } from 'sonner';
import { 
  FileText, Plus, User, Calendar, ChevronRight, Loader2, CheckCircle, 
  Clock, Flag, Edit, Archive, Trash2, RotateCcw, AlertTriangle, Shield
} from 'lucide-react';

const statusConfig = {
  acknowledged: { label: 'Verified', color: 'bg-green-500/20 text-green-400 border-green-500/30', icon: CheckCircle },
  pending_acknowledgment: { label: 'Pending Acknowledgment', color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', icon: Clock },
  draft: { label: 'Draft', color: 'bg-[#F5F8FF] text-[#5B6472] border-[#E8EDF5]', icon: FileText },
  flagged: { label: 'Flagged', color: 'bg-red-500/20 text-red-400 border-red-500/30', icon: Flag },
  archived: { label: 'Archived', color: 'bg-[#F5F8FF]/50 text-[#5B6472] border-[#E8EDF5]', icon: Archive },
};

const Records = () => {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const res = await employerAPI.getRecords('all');
      setRecords(res.data || []);
    } catch (error) {
      console.error('Error fetching records:', error);
      toast.error('Failed to load records');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!selectedRecord) return;
    setActionLoading(true);
    try {
      await employerAPI.archiveRecord(selectedRecord.id);
      toast.success('Record archived');
      setArchiveModalOpen(false);
      setSelectedRecord(null);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive record');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRestore = async (record) => {
    setActionLoading(true);
    try {
      await employerAPI.restoreRecord(record.id);
      toast.success('Record restored');
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore record');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRecord) return;
    setActionLoading(true);
    try {
      await employerAPI.deleteRecord(selectedRecord.id);
      toast.success('Record permanently deleted');
      setDeleteModalOpen(false);
      setSelectedRecord(null);
      fetchRecords();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete record');
    } finally {
      setActionLoading(false);
    }
  };

  const activeRecords = records.filter(r => r.status !== 'archived');
  const archivedRecords = records.filter(r => r.status === 'archived');

  const renderRecordCard = (record, i, isArchived = false) => {
    const status = statusConfig[record.status] || statusConfig.draft;
    const isImmutable = record.status === 'acknowledged';
    
    return (
      <div
        key={record.id}
        className="animate-fade-in"
        style={{ animationDelay: `${i * 0.05}s` }}
        data-testid={`record-${record.id}`}
      >
        <Card className={`bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-5 ${isArchived ? 'opacity-70' : ''}`}>
          <div className="flex items-center justify-between">
            <Link 
              to={`/employer/records/${record.id}`}
              className="flex items-center gap-4 flex-1 group"
            >
              {record.employee_photo ? (
                <img src={record.employee_photo} alt="" className="w-12 h-12 rounded-full object-cover border border-[#E8EDF5]" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-[#F5F8FF] border border-[#E8EDF5] flex items-center justify-center">
                  <User className="w-5 h-5 text-[#5B6472]" />
                </div>
              )}
              <div>
                <h3 className="font-semibold text-[#0A0F1E] group-hover:text-[#1B5AFF] transition-colors">
                  {record.employee_name || record.employee_email || 'Unknown Employee'}
                </h3>
                <p className="text-[#5B6472] text-sm">{record.job_title}</p>
                <div className="flex items-center gap-2 mt-1 text-[#5B6472] text-xs">
                  <Calendar className="w-3 h-3" />
                  {record.start_date} - {record.currently_employed ? 'Present' : record.end_date}
                </div>
              </div>
            </Link>
            
            <div className="flex items-center gap-3">
              {/* Status Badge */}
              <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${status.color}`}>
                <status.icon className="w-3.5 h-3.5" />
                {status.label}
              </span>
              
              {/* Immutable Badge for co-signed records */}
              {isImmutable && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20">
                  <Shield className="w-3 h-3" />
                  Immutable
                </span>
              )}
              
              {/* Actions */}
              {!isArchived && !isImmutable && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/employer/records/${record.id}/edit`);
                    }}
                    className="text-[#5B6472] hover:text-[#0A0F1E] hover:bg-[#F5F8FF] rounded-lg h-8 w-8 p-0"
                    title="Edit"
                    data-testid={`edit-record-${record.id}`}
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedRecord(record);
                      setArchiveModalOpen(true);
                    }}
                    className="text-[#5B6472] hover:text-amber-400 hover:bg-amber-500/10 rounded-lg h-8 w-8 p-0"
                    title="Archive"
                    data-testid={`archive-record-${record.id}`}
                  >
                    <Archive className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              {/* Archived Actions */}
              {isArchived && (
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      handleRestore(record);
                    }}
                    className="text-[#5B6472] hover:text-green-400 hover:bg-green-500/10 rounded-lg h-8 w-8 p-0"
                    title="Restore"
                    data-testid={`restore-record-${record.id}`}
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedRecord(record);
                      setDeleteModalOpen(true);
                    }}
                    className="text-[#5B6472] hover:text-red-400 hover:bg-red-500/10 rounded-lg h-8 w-8 p-0"
                    title="Delete Permanently"
                    data-testid={`delete-record-${record.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              <Link to={`/employer/records/${record.id}`}>
                <ChevronRight className="w-5 h-5 text-[#5B6472] hover:text-[#1B5AFF] transition-colors" />
              </Link>
            </div>
          </div>
        </Card>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="employer-records">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#0A0F1E]">VETT Records</h1>
            <p className="mt-2 text-[#5B6472]">Manage your employee work records.</p>
          </div>
          <Link to="/employer/records/new">
            <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl btn-glow" data-testid="new-record-btn">
              <Plus className="mr-2 h-4 w-4" />
              New Record
            </Button>
          </Link>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#F5F8FF] rounded-xl p-1">
            <TabsTrigger 
              value="active" 
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
            >
              Active Records ({activeRecords.length})
            </TabsTrigger>
            <TabsTrigger 
              value="archived" 
              className="data-[state=active]:bg-[#1B5AFF] data-[state=active]:text-white text-[#5B6472] hover:text-[#0A0F1E] px-4 py-2 rounded-lg"
            >
              Archived ({archivedRecords.length})
            </TabsTrigger>
          </TabsList>

          {/* Active Records */}
          <TabsContent value="active" className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-[#1B5AFF]" />
              </div>
            ) : activeRecords.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <FileText className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No records yet</h3>
                <p className="text-[#5B6472] mb-6">
                  Start by creating a VETT Record for a current or former employee.
                </p>
                <Link to="/employer/records/new">
                  <Button className="bg-[#1B5AFF] hover:bg-[#1B5AFF] text-[#FFFFFF] rounded-xl">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Your First Record
                  </Button>
                </Link>
              </Card>
            ) : (
              <div className="space-y-3">
                {activeRecords.map((record, i) => renderRecordCard(record, i, false))}
              </div>
            )}
          </TabsContent>

          {/* Archived Records */}
          <TabsContent value="archived" className="mt-6">
            {archivedRecords.length === 0 ? (
              <Card className="bg-[#FFFFFF] border border-[#E8EDF5] rounded-xl p-8 text-center">
                <Archive className="w-12 h-12 text-[#5B6472] mx-auto mb-4" />
                <h3 className="text-lg font-medium text-[#0A0F1E] mb-2">No archived records</h3>
                <p className="text-[#5B6472]">
                  Archived records will appear here. You can restore or permanently delete them.
                </p>
              </Card>
            ) : (
              <div className="space-y-3">
                {archivedRecords.map((record, i) => renderRecordCard(record, i, true))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Archive Confirmation Modal */}
        <Dialog open={archiveModalOpen} onOpenChange={setArchiveModalOpen}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Archive className="w-5 h-5 text-amber-400" />
                Archive Record
              </DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                Are you sure you want to archive this record? It will be moved to the archived section and can be restored later.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-[#F5F8FF] rounded-xl p-4 border border-[#E8EDF5]">
              <p className="text-[#0A0F1E] font-medium">{selectedRecord?.employee_name || selectedRecord?.employee_email}</p>
              <p className="text-[#5B6472] text-sm">{selectedRecord?.job_title}</p>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setArchiveModalOpen(false)}
                className="text-[#5B6472]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleArchive}
                disabled={actionLoading}
                className="bg-amber-500 hover:bg-amber-600 text-[#0A0F1E] rounded-xl"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Archive Record
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent className="bg-[#FFFFFF] border-[#E8EDF5] text-[#0A0F1E]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Permanently Delete Record
              </DialogTitle>
              <DialogDescription className="text-[#5B6472]">
                This action cannot be undone. The record will be permanently deleted.
              </DialogDescription>
            </DialogHeader>
            <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/30">
              <p className="text-[#0A0F1E] font-medium">{selectedRecord?.employee_name || selectedRecord?.employee_email}</p>
              <p className="text-[#5B6472] text-sm">{selectedRecord?.job_title}</p>
            </div>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setDeleteModalOpen(false)}
                className="text-[#5B6472]"
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                disabled={actionLoading}
                className="bg-red-500 hover:bg-red-600 hover:bg-red-700 text-[#FFFFFF] rounded-xl"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete Permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default Records;
